import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  House,
  Plus,
  PencilSimple,
  Trash,
  ArrowUp,
  ArrowDown,
  Check,
  X,
  FloppyDisk,
  Camera,
  ImageSquare,
} from "@phosphor-icons/react";
import { api, fileUrl } from "@/lib/api";

/**
 * Rooms management panel for HouseholdDetail (v3.15).
 *
 * v3.15.4 adds a per-room hero photograph. Owners+editors can click
 * the thumbnail on any room row to upload a cover; the "×" mini-button
 * removes it. Rename carries the photo across; delete + bulk-reorder
 * prune orphaned photo entries on the backend.
 */
export function RoomsPanel({ householdId, isOwner, canEdit = false }) {
  const [rooms, setRooms] = useState([]);
  const [roomPhotos, setRoomPhotos] = useState({}); // { lowercase_name: storage_path }
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // { idx, value }
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null); // lowercased room key
  const fileInputs = useRef({}); // per-row hidden <input type=file> refs

  const writable = isOwner || canEdit;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/households/${householdId}/rooms`);
      setRooms(r.data?.rooms || []);
      setRoomPhotos(r.data?.room_photos || {});
      setDirty(false);
    } catch (e) {
      toast.error("Couldn't load rooms.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [householdId]);

  const addNew = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const r = await api.post(`/households/${householdId}/rooms`, { name });
      setRooms(r.data?.rooms || []);
      setRoomPhotos(r.data?.room_photos || {});
      setNewName("");
      toast.success(`Added "${name}".`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add room.");
    } finally {
      setCreating(false);
    }
  };

  const commitRename = async (oldName) => {
    const next = editing?.value?.trim();
    if (!next || next === oldName) {
      setEditing(null);
      return;
    }
    try {
      const r = await api.patch(`/households/${householdId}/rooms`, { old: oldName, new: next });
      setRooms(r.data?.rooms || []);
      setRoomPhotos(r.data?.room_photos || {});
      const count = r.data?.assets_updated ?? 0;
      toast.success(
        count > 0
          ? `Renamed to "${next}" — updated ${count} asset${count === 1 ? "" : "s"}.`
          : `Renamed to "${next}".`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed.");
    } finally {
      setEditing(null);
    }
  };

  const remove = async (name) => {
    if (!window.confirm(`Remove "${name}" from the room list? Existing assets that reference it will keep their location text — the room just disappears from the picker.`)) {
      return;
    }
    try {
      const r = await api.delete(`/households/${householdId}/rooms/${encodeURIComponent(name)}`);
      setRooms(r.data?.rooms || []);
      setRoomPhotos(r.data?.room_photos || {});
      toast.success(`Removed "${name}".`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed.");
    }
  };

  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= rooms.length) return;
    const next = [...rooms];
    [next[idx], next[target]] = [next[target], next[idx]];
    setRooms(next);
    setDirty(true);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/households/${householdId}/rooms`, { rooms });
      setRooms(r.data?.rooms || []);
      setRoomPhotos(r.data?.room_photos || {});
      setDirty(false);
      toast.success("Room order saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (name, file) => {
    if (!file) return;
    const key = name.toLowerCase();
    setUploadingKey(key);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(
        `/households/${householdId}/rooms/${encodeURIComponent(name)}/photo`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setRoomPhotos(r.data?.room_photos || {});
      toast.success(`Cover saved for "${name}".`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cover upload failed.");
    } finally {
      setUploadingKey(null);
      // Reset the input so re-selecting the same file still fires onChange.
      const el = fileInputs.current[key];
      if (el) el.value = "";
    }
  };

  const clearPhoto = async (name) => {
    if (!window.confirm(`Remove the cover photo for "${name}"?`)) return;
    try {
      const r = await api.delete(`/households/${householdId}/rooms/${encodeURIComponent(name)}/photo`);
      setRoomPhotos(r.data?.room_photos || {});
      toast.success(`Cover removed from "${name}".`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed.");
    }
  };

  return (
    <section data-testid="rooms-panel" className="border border-[#E0DDD6] bg-white">
      <header className="border-b border-[#E0DDD6] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <House size={18} weight="bold" className="text-[#1A3622]" />
          <div>
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
              Rooms
            </div>
            <div className="text-sm text-[#0A192F]">
              The vocabulary the asset form's Room picker offers on this ledger — attach a hero photo per room for a mini-tour feel
            </div>
          </div>
        </div>
        {dirty && isOwner && (
          <button
            onClick={saveOrder}
            disabled={saving}
            data-testid="rooms-save-order"
            className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-4 py-2 text-xs font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-60"
          >
            <FloppyDisk size={12} weight="bold" />
            {saving ? "Saving…" : "Save Order"}
          </button>
        )}
      </header>

      {isOwner && (
        <div className="px-6 py-4 border-b border-[#E0DDD6] bg-[#F9F8F6]">
          <div className="flex gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNew(); } }}
              placeholder="Add a room… (e.g., Calvin's Bedroom)"
              data-testid="rooms-new-input"
              maxLength={60}
              className="flex-1 bg-white border border-[#E0DDD6] px-3 py-2 text-sm focus:outline-none focus:border-[#1A3622]"
            />
            <button
              onClick={addNew}
              disabled={!newName.trim() || creating}
              data-testid="rooms-add-btn"
              className="inline-flex items-center gap-2 bg-white text-[#0A192F] px-4 py-2 text-xs font-medium border border-[#0A192F] hover:bg-[#0A192F] hover:text-[#F9F8F6] disabled:opacity-50 disabled:pointer-events-none"
            >
              <Plus size={12} weight="bold" />
              Add
            </button>
          </div>
        </div>
      )}

      <ul data-testid="rooms-list">
        {loading && (
          <li className="px-6 py-4 text-xs text-[#5C5F58] italic">Loading rooms…</li>
        )}
        {!loading && rooms.length === 0 && (
          <li className="px-6 py-4 text-xs text-[#5C5F58] italic">
            No rooms yet. {isOwner ? "Add one above to get started." : "The owner can add rooms in this settings panel."}
          </li>
        )}
        {rooms.map((r, idx) => {
          const isEditing = editing?.idx === idx;
          const key = r.toLowerCase();
          const photoPath = roomPhotos?.[key];
          const isUploading = uploadingKey === key;
          return (
            <li
              key={`${r}-${idx}`}
              data-testid={`rooms-item-${r}`}
              className="px-6 py-3 border-b border-[#E0DDD6] last:border-b-0 flex items-center gap-3 hover:bg-[#F9F8F6]/50"
            >
              <span className="font-mono-ledger text-[10px] text-[#5C5F58] w-6 text-right">
                {String(idx + 1).padStart(2, "0")}
              </span>

              {/* Hero photo thumbnail (click to upload/replace) */}
              <div
                className="relative w-14 h-14 shrink-0 border border-[#E0DDD6] bg-[#F9F8F6] overflow-hidden group"
                data-testid={`rooms-photo-${r}`}
              >
                {photoPath ? (
                  <img
                    src={fileUrl(photoPath)}
                    alt={`${r} cover`}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#B8B4A8]">
                    <ImageSquare size={20} weight="regular" />
                  </div>
                )}
                {writable && (
                  <>
                    <button
                      type="button"
                      onClick={() => fileInputs.current[key]?.click()}
                      disabled={isUploading}
                      data-testid={`rooms-photo-upload-${r}`}
                      className="absolute inset-0 flex items-center justify-center bg-black/50 text-white text-[10px] opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-100"
                      aria-label={photoPath ? "Replace cover" : "Add cover"}
                    >
                      {isUploading ? (
                        <span className="text-[9px] uppercase tracking-widest">…</span>
                      ) : (
                        <Camera size={16} weight="bold" />
                      )}
                    </button>
                    <input
                      ref={(el) => { fileInputs.current[key] = el; }}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                      className="hidden"
                      onChange={(e) => uploadPhoto(r, e.target.files?.[0])}
                      data-testid={`rooms-photo-input-${r}`}
                    />
                    {photoPath && (
                      <button
                        type="button"
                        onClick={() => clearPhoto(r)}
                        data-testid={`rooms-photo-clear-${r}`}
                        className="absolute -top-1 -right-1 w-4 h-4 bg-[#6B1718] text-white text-[9px] leading-none flex items-center justify-center opacity-0 group-hover:opacity-100"
                        aria-label="Remove cover"
                      >
                        <X size={10} weight="bold" />
                      </button>
                    )}
                  </>
                )}
              </div>

              {isEditing ? (
                <>
                  <input
                    type="text"
                    value={editing.value}
                    autoFocus
                    onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); commitRename(r); }
                      if (e.key === "Escape") setEditing(null);
                    }}
                    maxLength={60}
                    data-testid={`rooms-rename-input-${r}`}
                    className="flex-1 bg-white border border-[#1A3622] px-2 py-1 text-sm focus:outline-none"
                  />
                  <button
                    onClick={() => commitRename(r)}
                    data-testid={`rooms-rename-save-${r}`}
                    className="p-1.5 text-[#1A3622] hover:bg-white border border-[#1A3622]"
                    aria-label="Save"
                  >
                    <Check size={12} weight="bold" />
                  </button>
                  <button
                    onClick={() => setEditing(null)}
                    className="p-1.5 text-[#5C5F58] hover:bg-white border border-[#E0DDD6]"
                    aria-label="Cancel"
                  >
                    <X size={12} weight="bold" />
                  </button>
                </>
              ) : (
                <>
                  <House size={12} weight="regular" className="text-[#1A3622]" />
                  <span className="flex-1 text-sm text-[#0A192F]">{r}</span>
                  {isOwner && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        data-testid={`rooms-up-${r}`}
                        className="p-1.5 text-[#5C5F58] hover:text-[#0A192F] hover:bg-white border border-transparent hover:border-[#E0DDD6] disabled:opacity-30 disabled:pointer-events-none"
                        aria-label="Move up"
                      >
                        <ArrowUp size={12} weight="bold" />
                      </button>
                      <button
                        onClick={() => move(idx, 1)}
                        disabled={idx === rooms.length - 1}
                        data-testid={`rooms-down-${r}`}
                        className="p-1.5 text-[#5C5F58] hover:text-[#0A192F] hover:bg-white border border-transparent hover:border-[#E0DDD6] disabled:opacity-30 disabled:pointer-events-none"
                        aria-label="Move down"
                      >
                        <ArrowDown size={12} weight="bold" />
                      </button>
                      <button
                        onClick={() => setEditing({ idx, value: r })}
                        data-testid={`rooms-edit-${r}`}
                        className="p-1.5 text-[#5C5F58] hover:text-[#1A3622] hover:bg-white border border-transparent hover:border-[#E0DDD6]"
                        aria-label="Rename"
                      >
                        <PencilSimple size={12} weight="bold" />
                      </button>
                      <button
                        onClick={() => remove(r)}
                        data-testid={`rooms-delete-${r}`}
                        className="p-1.5 text-[#5C5F58] hover:text-[#6B1718] hover:bg-white border border-transparent hover:border-[#6B1718]/40"
                        aria-label="Delete"
                      >
                        <Trash size={12} weight="bold" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default RoomsPanel;
